import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, ExternalLink, ShieldAlert } from "lucide-react";

const SEVERITY_COLORS = {
  high: "bg-red-100 text-red-800 border-red-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-yellow-50 text-yellow-800 border-yellow-200",
};

/**
 * Dialog de visualisation des anomalies détectées (TTC ≠ HT×TVA, dates, etc.).
 * Permet de naviguer vers la commande/client concerné pour correction.
 */
export const HealthAnomaliesDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ by_kind: [], total_anomalies: 0 });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.getHealthSummary();
        if (!cancelled) setData(res.data || { by_kind: [], total_anomalies: 0 });
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="health-anomalies-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            Qualité de la base — {data.total_anomalies} anomalie{data.total_anomalies > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            Vérifications automatiques des données. Cliquez sur une ligne pour ouvrir la commande / le client à corriger.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : data.by_kind.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Aucune anomalie détectée. Votre base est propre 👌
          </div>
        ) : (
          <div className="space-y-4">
            {data.by_kind.map((kind, ki) => (
              <Card key={ki} data-testid={`anomaly-${kind.kind}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className={`h-4 w-4 ${kind.severity === "high" ? "text-red-600" : kind.severity === "medium" ? "text-amber-600" : "text-yellow-600"}`} />
                      {kind.label}
                    </CardTitle>
                    <Badge variant="outline" className={SEVERITY_COLORS[kind.severity]}>
                      {kind.count}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="border-t">
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>Référence / Nom</th>
                          {kind.kind === "ttc_mismatch" && <>
                            <th className="text-right">TTC saisi</th>
                            <th className="text-right">TTC attendu</th>
                            <th className="text-right">Écart</th>
                          </>}
                          {kind.kind === "missing_order_date" && <th>Créée le</th>}
                          {kind.kind === "date_inconsistency" && <>
                            <th>Créée</th>
                            <th>Complétée</th>
                          </>}
                          {kind.kind === "no_assignment" && <th className="text-right">Montant HT</th>}
                          {kind.kind === "no_contact" && <th>Ville</th>}
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {kind.items.map((item) => (
                          <tr key={item.id || item.client_key}>
                            <td className="font-medium">
                              {item.reference || item.client_name || item.client_key}
                              {item.client_name && item.reference && (
                                <div className="text-xs text-muted-foreground">{item.client_name}</div>
                              )}
                            </td>
                            {kind.kind === "ttc_mismatch" && <>
                              <td className="text-right currency tabular-nums">{formatCurrency(item.amount_ttc)}</td>
                              <td className="text-right currency tabular-nums text-muted-foreground">{formatCurrency(item.expected_ttc)}</td>
                              <td className="text-right text-red-600 font-medium">{formatCurrency(item.amount_ttc - item.expected_ttc)}</td>
                            </>}
                            {kind.kind === "missing_order_date" && (
                              <td className="text-xs">{item.created_at?.slice(0, 10)}</td>
                            )}
                            {kind.kind === "date_inconsistency" && <>
                              <td className="text-xs">{item.created_at?.slice(0, 10)}</td>
                              <td className="text-xs text-red-600">{item.completed_at?.slice(0, 10)}</td>
                            </>}
                            {kind.kind === "no_assignment" && (
                              <td className="text-right currency tabular-nums">{formatCurrency(item.amount_ht)}</td>
                            )}
                            {kind.kind === "no_contact" && (
                              <td className="text-xs">{item.city || "—"}</td>
                            )}
                            <td>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (item.id) navigate(`/commandes?focus=${item.id}`);
                                  else if (item.client_key) navigate(`/clients`);
                                  onClose();
                                }}
                                title="Ouvrir"
                                data-testid={`anomaly-open-${item.id || item.client_key}`}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HealthAnomaliesDialog;
